'use client'

import { type ReactNode, useCallback, useEffect, useRef } from 'react'
import { useParams, useRouter } from 'next/navigation'

import { LAYOUT_TEST_IDS } from '@common/common-test-ids.const'
import { NotificationDrawer } from '@common/components/NotificationDrawer'

type Params = {
  openRightDrawer: (content: ReactNode) => void
  closeRightDrawer: () => void
}

export function useNotificationDrawer({ openRightDrawer, closeRightDrawer }: Params) {
  const router = useRouter()
  const { locale } = useParams()
  const isOpenRef = useRef(false)

  const handleClose = useCallback(() => {
    isOpenRef.current = false
    closeRightDrawer()
  }, [closeRightDrawer])

  const handleSettingsClick = useCallback(() => {
    isOpenRef.current = false
    closeRightDrawer()
    router.push(`/${locale}/settings/workspace/notifications`)
  }, [closeRightDrawer, locale, router])

  const openNotificationDrawer = useCallback(() => {
    isOpenRef.current = true
    openRightDrawer(
      <div data-testid={LAYOUT_TEST_IDS.notificationDrawer} style={{ display: 'contents' }}>
        <NotificationDrawer onClose={handleClose} onSettingsClick={handleSettingsClick} />
      </div>
    )
  }, [handleClose, handleSettingsClick, openRightDrawer])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpenRef.current || event.key !== 'Escape') return
      event.stopPropagation()
      handleClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleClose])

  return { openNotificationDrawer }
}
